/**
 * "Montar projeto" — guia de preparação que aparece na Visão Geral enquanto o
 * projeto está `planejado` (recém-criado). Resolve a fricção apontada no dossiê:
 * em vez de cair num workspace de 11 abas sem saber por onde começar, o usuário
 * segue um checklist: confirmar fases → atribuir responsáveis → revisar
 * cronograma → conferir carga. O passo de responsáveis é interativo aqui mesmo
 * (é o dado que alimenta o Gantt e a Carga de Pessoas).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, Circle, ListTree, Users, Calendar, BarChart3, ChevronUp, ChevronDown } from 'lucide-react';
import { fasesAPI } from '../../services/inovacaoAPI';
import { authAPI } from '../../services/api';
import { mensagemErro } from './labels';

const BRAND = 'var(--brand, #20a490)';

export default function MontarProjeto({ projeto, fases, recarregar, irParaTab }) {
  const [usuarios, setUsuarios] = useState([]);
  const [aberto, setAberto] = useState(true);

  useEffect(() => { authAPI.listUsers().then((r) => setUsuarios(r.data)).catch(() => {}); }, []);

  const fasesReais = fases.filter((f) => !f.eh_buffer);
  const total = fasesReais.length;
  const comResp = fasesReais.filter((f) => f.responsavel).length;
  const temFases = total > 0;
  const todosResp = temFases && comResp === total;
  const temDatas = fases.some((f) => f.data_fim_planejada);

  async function atribuir(fase, valor) {
    try {
      await fasesAPI.atualizar(fase.id, { responsavel_id: valor ? parseInt(valor) : null });
      await recarregar();
    } catch (e) { toast.error(mensagemErro(e, 'Erro ao atribuir responsável')); }
  }

  const passos = [
    { ok: temFases, label: temFases ? `${total} fases definidas` : 'Defina as fases do projeto' },
    { ok: todosResp, label: `${comResp}/${total} fases com responsável` },
    { ok: temDatas, label: temDatas ? `Entrega prevista: ${projeto.data_fim_planejada || '—'}` : 'Cronograma a calcular' },
    { ok: false, label: 'Conferir a carga das pessoas' },
  ];
  const feitos = passos.filter((p) => p.ok).length;

  return (
    <div style={{ background: '#fff', border: `1px solid ${BRAND}`, borderRadius: 12, marginBottom: 20, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', background: '#eafaf7', cursor: 'pointer' }}
           onClick={() => setAberto((o) => !o)}>
        <ListTree size={20} style={{ color: BRAND }} />
        <div style={{ flex: 1 }}>
          <strong style={{ color: '#16323a' }}>Montar o projeto</strong>
          <span style={{ color: '#5a6b72', fontSize: '0.85rem' }}> · {feitos}/4 etapas</span>
        </div>
        {aberto ? <ChevronUp size={18} style={{ color: '#5a6b72' }} /> : <ChevronDown size={18} style={{ color: '#5a6b72' }} />}
      </div>

      {aberto && (
        <div style={{ padding: 20 }}>
          <p style={{ marginTop: 0, color: '#7f8c8d' }}>
            Projeto recém-criado. Siga os passos abaixo para preparar a execução.
          </p>

          {/* Checklist resumo */}
          <div style={{ display: 'grid', gap: 6, marginBottom: 20 }}>
            {passos.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, color: p.ok ? '#27ae60' : '#7f8c8d' }}>
                {p.ok ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                <span style={{ fontSize: '0.9rem' }}>{p.label}</span>
              </div>
            ))}
          </div>

          {/* Passo 1 — fases */}
          <Bloco icone={<ListTree size={16} />} titulo="1. Fases & entregáveis"
                 acaoLabel="ajustar fases" onAcao={() => irParaTab('fases')}>
            {temFases ? `${total} fases (do template ou manuais). Revise se cobrem o trabalho.`
                      : 'Nenhuma fase ainda. Adicione na aba Fases.'}
          </Bloco>

          {/* Passo 2 — responsáveis (interativo) */}
          <Bloco icone={<Users size={16} />} titulo={`2. Responsáveis (${comResp}/${total})`}>
            <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
              {fasesReais.map((f) => (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ flex: 1, fontSize: '0.86rem', color: '#16323a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.ordem}. {f.nome}
                  </span>
                  <select value={f.responsavel?.id || ''} onChange={(e) => atribuir(f, e.target.value)}
                          style={{ padding: '4px 6px', borderRadius: 4, fontSize: '0.82rem', maxWidth: 180,
                                   border: f.responsavel ? '1px solid #ddd' : '1px solid #f0c089',
                                   background: f.responsavel ? '#fff' : '#fff8ef',
                                   color: f.responsavel ? '#2c3e50' : '#b9772a' }}>
                    <option value="">Sem responsável</option>
                    {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome_completo}</option>)}
                  </select>
                </div>
              ))}
              {!temFases && <span style={{ color: '#bdc3c7', fontSize: '0.85rem' }}>Defina as fases primeiro.</span>}
            </div>
          </Bloco>

          {/* Passo 3 — cronograma */}
          <Bloco icone={<Calendar size={16} />} titulo="3. Cronograma"
                 acaoLabel="ver cronograma" onAcao={() => irParaTab('cronograma')}>
            {temDatas ? `As datas já vêm calculadas a partir do início e das horas. Entrega prevista: ${projeto.data_fim_planejada || '—'}.`
                      : 'O cronograma é calculado quando as fases têm horas estimadas.'}
          </Bloco>

          {/* Passo 4 — carga */}
          <Bloco icone={<BarChart3 size={16} />} titulo="4. Carga das pessoas"
                 acaoLabelLink="/inovacao/carga-pessoas" acaoLabel="ver carga de pessoas">
            Depois de atribuir os responsáveis, confira se ninguém ficou sobrecarregado no período.
          </Bloco>

          {todosResp && (
            <div style={{ marginTop: 16, padding: '10px 14px', background: '#eafaf1', border: '1px solid #b9eccb',
                          borderRadius: 8, color: '#1e7e45', fontSize: '0.9rem' }}>
              ✓ Todas as fases têm responsável. O projeto está pronto para executar.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Bloco({ icone, titulo, children, acaoLabel, onAcao, acaoLabelLink }) {
  return (
    <div style={{ padding: '12px 0', borderTop: '1px solid #f0f4f5' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#16323a', fontWeight: 600, fontSize: '0.92rem' }}>
          <span style={{ color: BRAND }}>{icone}</span> {titulo}
        </div>
        {acaoLabel && acaoLabelLink && (
          <Link to={acaoLabelLink} style={{ color: BRAND, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{acaoLabel} →</Link>
        )}
        {acaoLabel && onAcao && (
          <button onClick={onAcao} style={{ background: 'none', border: 'none', color: BRAND, fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>{acaoLabel} →</button>
        )}
      </div>
      <div style={{ color: '#5a6b72', fontSize: '0.85rem', marginTop: 4 }}>{children}</div>
    </div>
  );
}
